// Pure sensor-calibration helpers for the installation wizard.
// Everything here is unit-tested; the step component stays a thin renderer.

export function emptyCalibration() {
  return {
    enabled: true,
    sensor_mount_type: "top",
    sensor_height_offset: "",
    scale_min_eng: "0",
    scale_max_eng: "",
  };
}

export function numberOrNull(value) {
  if (value == null || String(value).trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function calibrationError(value) {
  if (!value?.enabled) return "";
  if (!["top", "bottom"].includes(value.sensor_mount_type)) {
    return "Choose where the sensor is mounted.";
  }
  const offset = numberOrNull(value.sensor_height_offset);
  if (offset === null || offset < 0) {
    return "Enter the height from the tank floor to the sensor face (0 or more inches).";
  }
  if (value.sensor_mount_type === "top" && offset === 0) {
    return "A top-mounted sensor must be above the tank floor.";
  }
  if (numberOrNull(value.scale_min_eng) !== 0) {
    return "The empty point must be 0 (the tank floor) for this installation flow.";
  }
  const full = numberOrNull(value.scale_max_eng);
  if (full === null || full <= 0) {
    return "Enter the full point: tank floor to the bottom of the inlet pipe, in inches.";
  }
  return "";
}

// The interim save path replaces the whole configuration, so exactly these
// four backend fields are sent. scale_unit and invert_scale are derived
// server-side and must never be sent from the browser.
export function calibrationPayload(value) {
  if (!value?.enabled) return null;
  const error = calibrationError(value);
  if (error) throw new Error(error);
  return {
    sensor_mount_type: value.sensor_mount_type,
    sensor_height_offset: Number(value.sensor_height_offset),
    scale_min_eng: 0,
    scale_max_eng: Number(value.scale_max_eng),
  };
}

// Manual top-mount quick check: mirrors the legacy percent_level formula with
// a zero empty point. Not a live sensor reading, and never used for bottom
// mounts (their telemetry path is not reading + offset).
export function calibrationCheck(value, distanceValue) {
  if (!value?.enabled || value.sensor_mount_type !== "top" || calibrationError(value)) return null;
  const distance = numberOrNull(distanceValue);
  if (distance === null || distance < 0) return null;
  const raw = Number(value.sensor_height_offset) - distance;
  const height = Math.min(Number(value.scale_max_eng), Math.max(0, raw));
  return {
    height,
    percent: Math.round(height / Number(value.scale_max_eng) * 100),
    aboveFull: raw > Number(value.scale_max_eng),
    belowFloor: raw < 0,
  };
}
